import { Link } from "@tokenterminal/ui/Link"
import { ClientOnlySuspense } from "components/ClientOnlySuspense"
import { useAuth } from "context/Auth"
import { sendEvent } from "modules/analytics"
import {
  useAccess,
  ACCESS_COMPONENTS,
  ACCESS_ACTIONS,
} from "modules/auth/useAccess"
import { FeatureSwitch } from "modules/featureswitch/FeatureSwitch"
import { MasterExcelModal } from "modules/masterExcel/MasterExcelModal"
import { LazyAuthPaywallModal } from "modules/paywall/LazyAuthPaywallModal"
import { LazyProPaywallModal } from "modules/paywall/LazyProPaywallModal"
import {
  type HTMLAttributes,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react"
import { twMerge } from "tailwind-merge"

function ExternalArrowIcon() {
  return (
    <svg
      viewBox="0 0 8 8"
      xmlns="http://www.w3.org/2000/svg"
      className="w-[8px] h-[8px]"
    >
      <use href="#external-link" />
    </svg>
  )
}

function NavigationLink({
  href,
  children,
  className,
  ...props
}: {
  href: string
  target?: string
  className?: string
  children: ReactNode
}) {
  const isExternal = href?.startsWith("http") || href === "/"
  const Element = isExternal ? "a" : Link

  return (
    <Element
      href={href}
      className={twMerge("flex items-center py-2 gap-1", className)}
      {...props}
    >
      {children} {isExternal ? <ExternalArrowIcon /> : null}
    </Element>
  )
}

function IntercomButton() {
  const toggleRef = useRef(false)

  useEffect(() => {
    if (window.Intercom) {
      window.Intercom("onShow", function () {
        sendEvent("chat", { action: "open" })
        toggleRef.current = true
      })
      window.Intercom("onHide", function () {
        sendEvent("chat", { action: "close" })
        toggleRef.current = false
      })
    }
  }, [])

  return (
    <button
      className="flex items-center py-2 gap-2"
      onClick={() => window.Intercom?.(toggleRef.current ? "hide" : "show")}
    >
      <div className="block w-[4px] h-[4px] bg-emerald-500 dark:bg-emerald-400 rounded-full" />
      <span className="text-xs text-fg-secondary">Live chat</span>
    </button>
  )
}

function DownloadMasterExcelButton() {
  const hasAccess = useAccess(
    ACCESS_COMPONENTS.TERMINAL,
    ACCESS_ACTIONS.DOWNLOAD
  )
  const { isLoggedIn } = useAuth()

  const [isProModalOpen, setIsProModalOpen] = useState(false)
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false)

  return (
    <>
      <button
        className="flex items-center py-2 gap-2 text-xs text-fg-secondary"
        onClick={() => {
          if (hasAccess) {
            setIsDownloadModalOpen(true)
          } else {
            sendEvent("wall", {
              action: "download",
              wall_type: "pro",
              component: "master-sheet",
              name: "master-sheet",
              filters: {},
              page_type: "homepage",
            })

            setIsProModalOpen(true)
          }
        }}
      >
        Download Master sheet
      </button>

      <ClientOnlySuspense>
        <MasterExcelModal
          isOpen={isDownloadModalOpen}
          setIsOpen={setIsDownloadModalOpen}
          onClose={() => setIsDownloadModalOpen(false)}
        />
      </ClientOnlySuspense>

      {isLoggedIn ? (
        <LazyProPaywallModal
          type="download_master_sheet"
          featureName="Downloading the Master sheet"
          onClose={() => setIsProModalOpen(false)}
          isOpen={isProModalOpen}
          setIsOpen={setIsProModalOpen}
        />
      ) : (
        <LazyAuthPaywallModal
          onClose={() => setIsProModalOpen(false)}
          isOpen={isProModalOpen}
          setIsOpen={setIsProModalOpen}
        />
      )}
    </>
  )
}
export function Footer(props: HTMLAttributes<HTMLElement>) {
  return (
    <FeatureSwitch feature="FOOTER_ENABLED">
      <footer {...props}>
        <nav className="pb-4 md:pb-0 px-4 flex gap-4 w-full justify-between">
          <ul className="flex flex-row gap-x-2 gap-y-4 items-center justify-between gap-4">
            <li className="hidden lg:block">
              <IntercomButton />
            </li>
          </ul>
          <ul className="flex flex-row gap-x-2 gap-y-4 items-center justify-between gap-4">
            <li className="hidden lg:block">
              <DownloadMasterExcelButton />
            </li>
            <li>
              <NavigationLink
                href="https://docs.tokenterminal.com/"
                target="_blank"
              >
                <div className="text-fg-secondary text-xs">Docs</div>
              </NavigationLink>
            </li>
            <li>
              <NavigationLink
                href="https://docs.tokenterminal.com/reference/api-reference"
                target="_blank"
              >
                <div className="text-fg-secondary text-xs">API Reference</div>
              </NavigationLink>
            </li>
            <li>
              <NavigationLink href="https://tokenterminal.com" target="_blank">
                <div className="text-fg-secondary text-xs">
                  tokenterminal.com
                </div>
              </NavigationLink>
            </li>
          </ul>
        </nav>
      </footer>
    </FeatureSwitch>
  )
}
